import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './EventRounds.module.css';
import apiClient from '../../../services/apiClient';

interface Firm {
  Firm_ID: string;
  name: string;
  abbrev: string;
  logo?: string;
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  linkedin?: string;
  profile?: string;
  firm?: Firm;
  is_current_user?: boolean;
}

interface UserGroup {
  round: number;
  group_id: string;
  group_size: number;
  members: User[];
}

interface Event {
  Events_ID: string;
  title: string;
  sub_title?: string;
  event_date: string;
  location_name?: string;
  city?: string;
  country?: string;
}

interface EventRoundsData {
  event: Event;
  user_groups: UserGroup[];
}

const EventRounds: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<EventRoundsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  useEffect(() => {
    if (eventId) {
      fetchEventRounds();
    }
  }, [eventId]);

  const fetchEventRounds = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<EventRoundsData>(`/api/v2/web/events/${eventId}/rounds`);

      console.log('🔍 API Response:', response.data);
      
      setData(response.data);
      
      // Show success toast if groups were found
      if (response.data.user_groups && response.data.user_groups.length > 0) {
        const totalGroups = response.data.user_groups.length;
        const uniqueRounds = Array.from(new Set(response.data.user_groups.map((g: UserGroup) => g.round)));
        const roundsCount = uniqueRounds.length;
        toast.success(`Found ${totalGroups} group${totalGroups === 1 ? '' : 's'} across ${roundsCount} round${roundsCount === 1 ? '' : 's'}!`, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      console.error('Error fetching event rounds:', error);
      const errorMessage = await getErrorMessage(error);
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });

      setError(errorMessage);
      
      if (error?.status === 403 || errorMessage.includes('not been invited')) {
        setTimeout(() => {
          navigate('/events');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactAction = (action: string, user: User) => {
    switch (action) {
      case 'email':
        if (user.mail) {
          window.open(`mailto:${user.mail}`, '_blank');
        }
        break;
      case 'linkedin':
        if (user.linkedin) {
          window.open(user.linkedin, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'web':
        if (user.profile) {
          window.open(user.profile, '_blank', 'noopener,noreferrer');
        }
        break;
    }
  };

  const getProfileImage = (user: User): string => {
    if (user.picture) {
      return `data:image/png;base64,${user.picture}`;
    }
    return '/static/images/user-profile-icon.png';
  };

  const getCompanyLogo = (firm?: Firm): string => {
    if (firm?.logo) {
      return `data:image/png;base64,${firm.logo}`;
    }
    return '/static/images/No_image_available.png';
  };

  if (loading) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className={styles.loadingSpinner}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="Root">
        <div className="container mt-4">
          <div className={`alert alert-danger ${styles.errorAlert}`} role="alert">
            <h4 className="alert-heading">Error loading your matches</h4>
            <p>{error || 'Failed to load event data'}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/events')}
            >
              Back to Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Group user_groups by round
  const groupedByRound = data.user_groups.reduce((acc, group) => {
    if (!acc[group.round]) {
      acc[group.round] = [];
    }
    acc[group.round].push(group);
    return acc;
  }, {} as Record<number, UserGroup[]>);

  // Sort rounds numerically
  const sortedRounds = Object.keys(groupedByRound)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="Root">
      <div className="container mt-4">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/events/" className="text-decoration-none">Events</a>
            </li>
            <li className="breadcrumb-item">
              <span>{data.event.title}</span>
            </li>
            <li className="breadcrumb-item">
              <a href={`/events/${data.event.Events_ID}/participants`} className="text-decoration-none">
                Choose your Matches
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href={`/events/${data.event.Events_ID}/vote`} className="text-decoration-none">
                Your Match Ranking
              </a>
            </li>
            <li className="breadcrumb-item active">
              <span>Your Matches</span>
            </li>
          </ol>
        </nav>

        {/* Main Content Card */}
        <div className="card p-4" style={{ width: '100%' }}>
          <div className={styles.roleHeader}>
            <i className="bi bi-people-fill"></i>
            <h4 className="mb-0">
              Event - {data.event.title} - <strong>Your Matches</strong>
            </h4>
            {data.event.sub_title && (
              <p>
                <i 
                  className="bi bi-info text-primary" 
                  data-bs-toggle="popover" 
                  data-bs-placement="top"
                  data-bs-content={data.event.sub_title}
                  data-bs-html="true" 
                  tabIndex={0}
                ></i>
              </p>
            )}
          </div>
          <hr />

          <div className={styles.roundsContainer}>
                    {data.user_groups && data.user_groups.length > 0 ? (
                      sortedRounds.map((roundNumber, index) => (
                        <div key={`round-${roundNumber}`} className={`card p-4 shadow-sm mb-4 ${styles.roundContainer}`}>
                          <div className={styles.roundHeader}>
                            <h2 className={styles.roundTitle}>
                              <i className="bi bi-clock"></i> Round {roundNumber}
                            </h2>
                            <span className={styles.roundBadge}>
                              {groupedByRound[roundNumber].length} group{groupedByRound[roundNumber].length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          
                          {groupedByRound[roundNumber].map((group, groupIndex) => (
                            <div key={`${roundNumber}-${groupIndex}`} className={styles.group}>
                              <div className={styles.groupHeader}>
                                <h5 className={styles.groupTitle}>
                                  <i className="bi bi-people"></i> Group {groupIndex + 1}
                                </h5>
                                <span className={styles.groupSize}>{group.members.length}/{group.group_size} members</span>
                              </div>
                              
                              <div className={styles.groupMembers}>
                                {group.members.map((member, memberIndex) => (
                                  <div 
                                    key={`${group.group_id}-${memberIndex}`} 
                                    className={`${styles.member} ${member.is_current_user ? styles.currentUserMember : ''}`}
                                  >
                                    {member.is_current_user && (
                                      <div className={styles.currentUserBadge}>
                                        <i className="bi bi-star-fill"></i> You
                                      </div>
                                    )}
                                    <div className={styles.profileSection}>
                                      <img 
                                        src={getProfileImage(member)} 
                                        className={styles.userImg} 
                                        alt={`${member.firstname} ${member.lastname}`}
                                      />
                                      <div className={styles.userInfo}>
                                        <div className={styles.userName}>
                                          {[member.title, member.firstname, member.lastname]
                                            .filter(Boolean)
                                            .join(' ')}
                                        </div>
                                        {member.jobtitle && (
                                          <div className={styles.userJobTitle}>
                                            {member.jobtitle}
                                          </div>
                                        )}
                                        {member.firm && (
                                          <div className={styles.userFirm}>
                                            <img 
                                              src={getCompanyLogo(member.firm)} 
                                              className={styles.companyLogo}
                                              alt={member.firm.abbrev || 'Company Logo'}
                                            />
                                            <span>{member.firm.name || member.firm.abbrev}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className={styles.contactActions}>
                                      <button
                                        onClick={() => handleContactAction('email', member)}
                                        className={`${styles.action} ${styles.email}`}
                                        disabled={!member.mail}
                                      >
                                        <i className="bi bi-envelope"></i> Email
                                      </button>
                                      
                                      {member.profile && (
                                        <button
                                          onClick={() => handleContactAction('web', member)}
                                          className={`${styles.action} ${styles.web}`}
                                        >
                                          <i className="bi bi-globe"></i> Web
                                        </button>
                                      )}
                                      
                                      {member.linkedin && (
                                        <button
                                          onClick={() => handleContactAction('linkedin', member)}
                                          className={`${styles.action} ${styles.linkedin}`}
                                        >
                                          <i className="bi bi-linkedin"></i> LinkedIn
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>
                        <div className="text-center py-5">
                          <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
                          <h5 className="text-muted mt-3">No matches available yet</h5>
                          <p className="text-muted">Your matches will appear here once they have been created for this event.</p>
                        </div>
                      </div>
                    )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventRounds;
